import { EyeOutlined } from "@ant-design/icons";
import { Button, Descriptions, Image, Modal } from "antd";
import type React from "react";
import { useMemberDetail } from "../hooks/useEventDetail";
import type { MemberAttribute, MemberDetail } from "../types";

interface MemberDetailModalProps {
	open: boolean;
	onClose: () => void;
	memberId?: number;
	registration?: MemberDetail;
	primaryColor?: string;
}

const DEFAULT_TEXT_NONE_DISPLAY = "ー";

export const MemberDetailModal: React.FC<MemberDetailModalProps> = ({
	open,
	onClose,
	memberId,
	registration,
	primaryColor = "#99CA29",
}) => {
	const { data: member, isLoading } = useMemberDetail(
		open ? memberId : undefined,
	);

	const attributes = member?.attributes || [];
	const addressAttributes = attributes.filter((attr) =>
		attr.type?.startsWith("address"),
	);
	const otherAttributes = attributes.filter(
		(attr) => !attr.type?.startsWith("address"),
	);

	const fullAddress = [
		addressAttributes.find((a) => a.type === "address_postal")?.value
			? `〒${addressAttributes.find((a) => a.type === "address_postal")?.value}`
			: "",
		addressAttributes.find((a) => a.type === "address_prefecture")?.value,
		addressAttributes.find((a) => a.type === "address_city")?.value,
		addressAttributes.find((a) => a.type === "address_address")?.value,
		addressAttributes.find((a) => a.type === "address_building")?.value,
	]
		.filter(Boolean)
		.join("、");

	const displayData = member || registration;
	const isFromRegistration = !member && registration;

	return (
		<Modal
			title={<span style={{ color: primaryColor }}>お客様詳細</span>}
			open={open}
			onCancel={onClose}
			footer={
				<div style={{ display: "flex", justifyContent: "center" }}>
					<Button size="large" onClick={onClose} style={{ padding: "0 48px" }}>
						閉じる
					</Button>
				</div>
			}
			styles={{ body: { maxHeight: "90vh", overflowY: "auto" } }}
			centered
			destroyOnClose
		>
			{isLoading ? (
				<div style={{ padding: "40px", textAlign: "center" }}>
					読み込み中...
				</div>
			) : displayData ? (
				<div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
					<div style={{ display: "flex", justifyContent: "center" }}>
						{displayData.picUrl ? (
							<Image
								preview={{ mask: <EyeOutlined /> }}
								width={100}
								height={100}
								style={{
									borderRadius: "50%",
									border: "4px solid white",
									objectFit: "cover",
								}}
								src={
									displayData.picUrl.startsWith("http")
										? `${displayData.picUrl}/large`
										: displayData.picUrl
								}
								fallback="/no-image.png"
							/>
						) : (
							<img
								src="/no-image.png"
								width={100}
								height={100}
								style={{
									borderRadius: "50%",
									border: "4px solid white",
									objectFit: "cover",
								}}
								alt=""
							/>
						)}
					</div>

					<Descriptions column={1} layout="horizontal" bordered>
						<Descriptions.Item label="LINE名" style={{ textAlign: "center" }}>
							{displayData.displayName ||
								(displayData.lastName
									? `${displayData.lastName} ${displayData.firstName || ""}`
									: "") ||
								DEFAULT_TEXT_NONE_DISPLAY}
						</Descriptions.Item>
						<Descriptions.Item label="フリガナ" style={{ textAlign: "center" }}>
							{(displayData.lastNameKana
								? `${displayData.lastNameKana} ${displayData.firstNameKana || ""}`
								: "") || DEFAULT_TEXT_NONE_DISPLAY}
						</Descriptions.Item>
						{!isFromRegistration && (
							<Descriptions.Item
								label="会員コード"
								style={{ textAlign: "center" }}
							>
								{displayData.memberCode || DEFAULT_TEXT_NONE_DISPLAY}
							</Descriptions.Item>
						)}

						{(displayData.telephone || isFromRegistration) && (
							<Descriptions.Item
								label="電話番号"
								style={{ textAlign: "center" }}
							>
								{displayData.telephone || DEFAULT_TEXT_NONE_DISPLAY}
							</Descriptions.Item>
						)}
						{(displayData.email || isFromRegistration) && (
							<Descriptions.Item
								label="メールアドレス"
								style={{ textAlign: "center" }}
							>
								{displayData.email || DEFAULT_TEXT_NONE_DISPLAY}
							</Descriptions.Item>
						)}

						<Descriptions.Item label="住所" style={{ textAlign: "center" }}>
							{isFromRegistration
								? [
										displayData.postalCode ? `〒${displayData.postalCode}` : "",
										displayData.prefecture,
										displayData.city,
										displayData.address,
										displayData.building,
									]
										.filter(Boolean)
										.join("、") || DEFAULT_TEXT_NONE_DISPLAY
								: fullAddress || DEFAULT_TEXT_NONE_DISPLAY}
						</Descriptions.Item>

						{!isFromRegistration &&
							otherAttributes.map((attr) => {
								if (attr.type === "image") {
									const imgUrl = attr.value
										? `/uploads/members/memberAttribute${attr.id}/${attr.value}`
										: "";
									return (
										<Descriptions.Item
											key={attr.id}
											label={attr.name}
											style={{ textAlign: "center" }}
										>
											{imgUrl ? (
												<Image
													src={imgUrl}
													alt={attr.name}
													style={{
														maxWidth: "100%",
														maxHeight: "200px",
														objectFit: "contain",
													}}
													fallback="/no-image.png"
													preview={true}
												/>
											) : (
												DEFAULT_TEXT_NONE_DISPLAY
											)}
										</Descriptions.Item>
									);
								}

								return (
									<Descriptions.Item
										key={attr.id}
										label={attr.name}
										style={{ textAlign: "center" }}
									>
										{attr.value || DEFAULT_TEXT_NONE_DISPLAY}
									</Descriptions.Item>
								);
							})}
					</Descriptions>
				</div>
			) : (
				<div style={{ padding: "40px", textAlign: "center" }}>
					データがありません
				</div>
			)}
		</Modal>
	);
};
